import $ from "jquery";

import * as analytics from "analytics";
import * as experiment from "experiment";
import * as localAPI from "localAPI";
import * as notificationBar from "notificationBar";
import * as readerColorScheme from "readerColorScheme";
import * as readerPageNavigation from "readerPageNavigation";
import * as readerReviewUtils from "readerReviewUtils";
import * as readerTokens from "readerText/tokens";
import readlangClient from "readlangClient";
import * as bookUtils from "shared/bookUtils";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import * as sidebarSettings from "sidebarExpandedSetting";
import * as spanUtils from "spanUtils";
import BookStore from "stores/BookStore";
import ReaderTextStore from "stores/ReaderTextStore";
import ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";
import type { ContextTranslationUsage } from "views/ContextTranslationStrip";
import * as walkthrough from "walkthrough";
import type ReaderTextController from "./ReaderTextController";

const pageName = "readerPage";

const connectionErrorMessageLines = [
  "Couldn't load this text.",
  "Please check your connection and reload to try again.",
];

export type PreparedReaderPageRender = {
  cancelled?: boolean;
  currentReaderTextStore?: ReaderTextStore;
  currentReaderViewStore?: ReaderViewStore;
  errorMessageLines?: string[];
  initialized?: boolean;
  pageData?: any;
};

type CompletePreactRenderCallbacks = {
  initializeFormattingOptions?: () => Promise<void> | void;
  initializeSyncAudio?: (activeReaderViewStore: ReaderViewStore) => void;
  initializeReadAloud?: (activeReaderViewStore: ReaderViewStore) => void;
  updateSyncAudioOptions?: (options: any) => void;
};

type ReaderPageLoaderDeps = {
  getAppRouter: () => any;
  getCurrentTabName: () => string | undefined;
  getOptions: () => any;
  getPageData: () => any;
  getReaderTextController: () => ReaderTextController;
  getRenderedCallback: () => ((renderedOptions?: any) => void) | undefined;
  isOwner: (book?: any) => boolean;
  patchPageData: (patch: Record<string, any>) => void;
  removeReaderWordPopUp: () => void;
  requestReaderPageRerender: () => void;
  setAppRouter: (appRouter: any) => void;
  setOptions: (options: any) => void;
  setPageData: (pageData: any) => void;
  setRenderedCallback: (
    renderedCallback: ((renderedOptions?: any) => void) | undefined
  ) => void;
  updateLayout: () => void;
};

export const createReaderPageLoader = (deps: ReaderPageLoaderDeps) => {
  let body: JQuery<HTMLElement> | undefined;
  let readerViewStore: ReaderViewStore | null = null;
  let readerTextStore: ReaderTextStore | null = null;
  const disposers: Array<() => void> = [];

  const shouldShowContextTranslationStrip = async (): Promise<boolean> => {
    if (!stores.user?.id || stores.user.isLocalMode.value) {
      return false;
    }
    if (await localAPI.isLocalMode()) {
      return false;
    }
    if (stores.user.hasPremiumAccess.value) {
      return false;
    }
    return (
      experiment.getConditionAndTreat("freeContextTranslations2") ===
      "experiment"
    );
  };

  const removeContextTranslationStrip = () => {
    if (!body?.hasClass("showContextTranslationStrip")) {
      return;
    }
    body.removeClass("showContextTranslationStrip");
    deps.updateLayout();
  };

  const prepareContextTranslationStripIfEligible = async (
    sourceLanguage: string
  ): Promise<ContextTranslationUsage | undefined> => {
    if (!(await shouldShowContextTranslationStrip())) {
      body?.removeClass("showContextTranslationStrip");
      return undefined;
    }

    // Default to ON if the user has never explicitly set the flag.
    if (stores.user.featureFlags.value?.contextTranslations === undefined) {
      stores.user.setFeature("contextTranslations", true);
    }

    try {
      const response = await readlangClient.request(
        "GET",
        "/api/translate/contextUsage",
        { source: sourceLanguage },
        undefined,
        undefined
      );
      const { remaining, limit, eligible } = response.data;
      if (!eligible) {
        body?.removeClass("showContextTranslationStrip");
        return undefined;
      }

      body?.addClass("showContextTranslationStrip");
      return { remaining, limit };
    } catch (_error) {
      body?.removeClass("showContextTranslationStrip");
      return undefined;
    }
  };

  const setupContextTranslationStripSubscription = () => {
    disposers.push(
      stores.user.onChange(
        (a) => a.subscriptionPlan,
        () => {
          if (stores.user.hasPremiumAccess.value) {
            removeContextTranslationStrip();
          }
        }
      )
    );
  };

  const getBookPage = async (
    innerOptions,
    activeReaderViewStore: ReaderViewStore,
    activeBookModel: BookStore
  ) => {
    const appRouter = deps.getAppRouter();
    const book = activeBookModel.book.value;
    let toPosition;

    let currentPosition = 0;
    let furthestPosition = 0;
    const spansPerPage = 1000;
    let percentage;

    const userBook = book.userBook;

    if (innerOptions.endOfText) {
      return {
        user: { ...stores.user.state.value },
        book,
        title: book.title,
        audio: book.audioURL != null && book.audioURL.length > 0,
        fromPosition: activeReaderViewStore.fromPosition.value,
        furthestPosition: furthestPosition,
        percentage: percentage,
        contentHTML: "",
        endOfBook: innerOptions.endOfText,
        owner: deps.isOwner(book),
        allowYouTube: book.metrics?.words <= bookUtils.maxYouTubeWords,
        public: book.public,
      };
    }

    if (userBook) {
      if ("readTo" in userBook) {
        furthestPosition = parseInt(String(userBook.readTo), 10);
      }
      if ("position" in userBook) {
        currentPosition = parseInt(String(userBook.position), 10);
      } else {
        currentPosition = furthestPosition;
      }

      currentPosition = Math.min(currentPosition, book.totalWords - 1);
    }

    if ("scroll" in innerOptions) {
      activeReaderViewStore.setFromPosition(0);
      toPosition = book.totalWords;
    } else {
      if (
        (innerOptions.from && innerOptions.from > book.totalWords - 1) ||
        (innerOptions.to && innerOptions.to > book.totalWords)
      ) {
        delete innerOptions.from;
        innerOptions.to = book.totalWords;
      }

      if ("to" in innerOptions) {
        toPosition = parseInt(innerOptions.to || 0, 10);
        activeReaderViewStore.setFromPosition(
          Math.max(0, toPosition - spansPerPage)
        );
        percentage = "TODO: calc percentage before page render";
      } else {
        if ("from" in innerOptions) {
          activeReaderViewStore.setFromPosition(
            parseInt(innerOptions.from || 0, 10)
          );
        } else {
          appRouter.navigate(
            "library/" + innerOptions.bookID + "/from/" + currentPosition,
            { replace: true }
          );
          activeReaderViewStore.setFromPosition(currentPosition);
        }
        percentage = Math.round(
          (100 * activeReaderViewStore.fromPosition.value) / book.totalWords
        );
        toPosition = activeReaderViewStore.fromPosition.value + spansPerPage;
      }
      if (activeReaderViewStore.fromPosition.value >= book.totalWords) {
        activeReaderViewStore.setFromPosition(book.totalWords - 1);
      }
      if (activeReaderViewStore.fromPosition.value > furthestPosition) {
        furthestPosition = activeReaderViewStore.fromPosition.value;
      }
    }

    let htmlFragment = await activeBookModel.getHtmlFragment(
      activeReaderViewStore.fromPosition.value - 1,
      toPosition,
      readerReviewUtils.isActive(stores)
    );
    // The next page may run on into the next chunk: have it ready
    activeBookModel.prefetchChunkAfter(toPosition);

    const contextBeforePage =
      activeReaderViewStore.fromPosition.value === 0
        ? ""
        : await (async () => {
            let beforePageContextHtml = await activeBookModel.getHtmlFragment(
              Math.max(
                0,
                activeReaderViewStore.fromPosition.value -
                  Math.floor(spanUtils.maxContextWords / 2)
              ),
              activeReaderViewStore.fromPosition.value,
              false
            );
            const fromPositionIndex = beforePageContextHtml.indexOf(
              `<span data-p=${activeReaderViewStore.fromPosition.value}>`
            );
            beforePageContextHtml = beforePageContextHtml.substring(
              0,
              fromPositionIndex
            );
            const text = beforePageContextHtml
              .replace(/<\/p><p>/g, "\n")
              .replace(/<[^>]+>/g, "");

            const lastSentenceBreakIndex = text.search(
              spanUtils.lastSentenceBreakRegExp()
            );

            return utils.trimStart(text.substring(lastSentenceBreakIndex + 1));
          })();

    if (stores.user.state.value.readerSentenceBreaks === "on") {
      htmlFragment = htmlFragment
        .replace(/([?!.])<\/span>([^?!.\w<])/g, "$1</span><br/>$2")
        .replace(/([?!.])([^?!.\w<])/g, "$1<br/>$2")
        .replace(
          new RegExp(
            `([${spanUtils.noSpaceSentenceBreakCharacters.join("")}]+”?)`,
            "g"
          ),
          "$1<br/>"
        );
    }

    const startRegExp = new RegExp(
      '<div class="sectionBreak">' +
        "|(<[hp][1-9]?>.*|[^ >]*)<span data-p=" +
        activeReaderViewStore.fromPosition.value +
        ">"
    );
    const startIndex = htmlFragment.search(startRegExp);

    if (startIndex !== -1) {
      htmlFragment = htmlFragment.substring(startIndex);
    }

    const firstClosingPTag = htmlFragment.indexOf("</p>");
    const firstOpeningPTag = htmlFragment.indexOf("<p>");
    if (
      firstClosingPTag !== -1 &&
      (firstOpeningPTag === -1 || firstOpeningPTag > firstClosingPTag)
    ) {
      htmlFragment = "<p>" + htmlFragment;
    }

    const lastClosingPTag = htmlFragment.lastIndexOf("</p>");
    const lastOpeningPTag = htmlFragment.lastIndexOf("<p>");
    if (lastOpeningPTag !== -1 && lastClosingPTag < lastOpeningPTag) {
      htmlFragment = htmlFragment + "</p>";
    }

    const localMode = await localAPI.isLocalMode();
    const languageString =
      languageMap.map[book.language]?.english ?? book.language;
    const showSignedOutTopBar =
      localMode && !walkthrough.hasAchievement("setLanguages");

    const pageData = {
      user: { ...stores.user.state.value },
      book,
      title: book.title,
      audio: book.audioURL != null && book.audioURL.length > 0,
      fromPosition: activeReaderViewStore.fromPosition.value,
      furthestPosition: furthestPosition,
      percentage: percentage,
      contentHTML: htmlFragment,
      endOfBook: innerOptions.endOfText,
      scrollMode: "scroll" in innerOptions,
      owner: deps.isOwner(book),
      hasAudioFile: !!book.audioFileID,
      // metrics.words is computed with bookUtils.tokenizeWords — the same
      // word definition the server's YouTube word-limit guards use — so this
      // flag always agrees with whether the API would accept the pairing.
      allowYouTube: book.metrics?.words <= bookUtils.maxYouTubeWords,
      public: book.public,
      showSignedOutTopBar,
      signedOutTopBarLanguageString: languageString,
      contextBeforePage,
    };

    if (
      stores.user.state.value.subscriptionPlan === "standard" ||
      stores.user.state.value.subscriptionPlan === "plus" ||
      stores.user.state.value.subscriptionPlan === "freeForever"
    ) {
      (pageData as any).upgraded = true;
    }

    if (pageData.book.url && pageData.book.url.length > 0) {
      (pageData as any).publicUrl = pageData.book.url;
    } else {
      pageData.book.urlExists = false;
    }

    if (pageData.public && !(pageData as any).publicUrl) {
      (pageData as any).publicUrl =
        "http://" + window.location.hostname + "/library/" + pageData.book._id;
    }

    if (localMode) {
      (pageData as any).localMode = true;
    }
    return pageData;
  };

  const onVisibilityChange = async () => {
    if (document.visibilityState === "visible") {
      const previousPosition = stores.bookModel.book.value.userBook?.position;

      if (previousPosition !== undefined) {
        await stores.bookModel.fetch();
        const newPosition = stores.bookModel.book.value.userBook?.position;
        if (newPosition > previousPosition) {
          notificationBar.showMessage({
            htmlMessage:
              "It looks like you've read more of this book on a different browser tab. " +
              `<a id="click-to-advance" href="/library/${stores.bookModel.id}/from/${newPosition}">Click here to advance</a>.`,
            element: $(".container"),
            time: 1e8,
            callback: () => {
              $("#click-to-advance").on("click", (event) => {
                // goToLocation keeps scroll-mode (video) readers on their
                // /scroll route - the href's default /from navigation would
                // blank the mounted reader. The raw href stays for open-in-
                // new-tab, where a cold load self-heals via enterScrollMode.
                event.preventDefault();
                readerPageNavigation.goToLocation(
                  "from",
                  Number(newPosition),
                  deps.getAppRouter()
                );
                notificationBar.dismiss();
              });
            },
          });
        }
      }
    }
  };

  const preparePreactRender = async (
    _renderedCallback,
    _appRouter,
    _options
  ): Promise<PreparedReaderPageRender> => {
    const nextOptions = { ..._options };
    deps.setOptions(nextOptions);
    const options = deps.getOptions();
    deps.setAppRouter(_appRouter);
    deps.setRenderedCallback(_renderedCallback);

    if (window.location.pathname.endsWith("/end")) {
      options.endOfText = true;
    }

    deps.getReaderTextController().resetForPrepare();
    deps.getReaderTextController().setLayoutDisabled(true);

    body = $("body");

    document.addEventListener("visibilitychange", onVisibilityChange);

    if (!readerViewStore) {
      readerViewStore = new ReaderViewStore();
    }
    // A text store is bound to one tokenization: a fresh instance per page
    // load makes stale position-keyed state (groups, selections) impossible
    // by construction. The island keeps rendering the outgoing page from the
    // old store until the new page's state lands.
    readerTextStore?.close();
    readerTextStore = new ReaderTextStore();
    const currentReaderViewStore = readerViewStore;
    const currentReaderTextStore = readerTextStore;

    deps.removeReaderWordPopUp();

    let initialized = false;
    if (body.attr("id") === pageName) {
      if (
        stores.bookModel &&
        stores.bookModel.book.value._id.toString() === options.bookID.toString()
      ) {
        initialized = true;
      }
    } else {
      body.attr("id", pageName);
      readerColorScheme.applyToBody();
    }

    walkthrough.setAchievement("readText", true);

    let currentBookModel = stores.bookModel;

    if (!initialized) {
      currentBookModel = new BookStore({ _id: options.bookID });
      stores.bookModel = currentBookModel;

      try {
        await currentBookModel.fetch();
      } catch (error) {
        if (error?.jqXHR?.alreadyNotified) {
          // Terminal failure (network loss, 5xx): show a connection error
          // rather than the false-specific "Article not found"
          return { errorMessageLines: connectionErrorMessageLines };
        }
        // fetch() rejects with { jqXHR, textStatus, errorThrown }
        if (error?.jqXHR?.status === 403) {
          if (await localAPI.isLocalMode()) {
            const errorMessageLines = [
              "This document doesn't belong to you.",
              "Either login, or ask the owner of the document to share it publicly.",
            ];
            readlangClient.login();
            return { errorMessageLines };
          } else {
            return {
              errorMessageLines: [
                "This document doesn't belong to you.",
                "Ask the owner of the document to share it publicly.",
              ],
            };
          }
        } else {
          analytics.trackEvent("error-articleNotFound");
          return { errorMessageLines: ["Uh oh. Article not found."] };
        }
      }

      if (
        readerViewStore !== currentReaderViewStore ||
        stores.bookModel !== currentBookModel
      ) {
        return { cancelled: true };
      }
    }

    if (
      stores.user.currentLearningLanguage.value !==
      currentBookModel.book.value.language
    ) {
      stores.user.save({
        currentLearningLanguage: currentBookModel.book.value.language,
      });
    }

    if (!initialized) {
      readerReviewUtils.reset();
    }

    let nextPageData: any;
    try {
      nextPageData = await getBookPage(
        options,
        currentReaderViewStore,
        currentBookModel
      );
    } catch (error: any) {
      if (error?.jqXHR?.alreadyNotified) {
        // Terminal chunk-fetch failure (network loss, 5xx): show an error
        // page rather than committing empty/partial content as the new page
        return { errorMessageLines: connectionErrorMessageLines };
      }
      throw error;
    }
    if (
      readerViewStore !== currentReaderViewStore ||
      stores.bookModel !== currentBookModel
    ) {
      return { cancelled: true };
    }

    // Parse the spannified HTML into the token tree that the Preact text
    // island renders. Set before the page state lands so the island's first
    // render already has the new page's tokens.
    currentReaderTextStore.setTokens(
      nextPageData.contentHTML
        ? readerTokens.parseReaderText(
            nextPageData.contentHTML,
            currentBookModel.book.value.language
          )
        : undefined
    );

    nextPageData.selectedTabName =
      deps.getCurrentTabName() ?? sidebarSettings.getTab();
    nextPageData.contextTranslationUsage =
      await prepareContextTranslationStripIfEligible(
        currentBookModel.book.value.language
      );
    if (
      readerViewStore !== currentReaderViewStore ||
      stores.bookModel !== currentBookModel
    ) {
      return { cancelled: true };
    }

    deps.setPageData(nextPageData);

    await Promise.resolve($.ready);

    if (
      readerViewStore !== currentReaderViewStore ||
      stores.bookModel !== currentBookModel
    ) {
      return { cancelled: true };
    }

    return {
      currentReaderTextStore,
      currentReaderViewStore,
      initialized,
      pageData: nextPageData,
    };
  };

  const setupPageTextIsland = (options: any) => {
    deps.getReaderTextController().setupPageTextIsland(options);
  };

  const setupReaderMode = (options: any) => {
    deps.getReaderTextController().setupReaderMode(options);
  };

  const setupTextInteractions = (options: any) => {
    deps.getReaderTextController().setupTextInteractions(options);
  };

  const handleResize = () => {
    deps.updateLayout();
  };

  const finishReaderPageLayout = (initialized: boolean) => {
    if (!initialized && deps.getReaderTextController().hasPageTextViewport()) {
      window.addEventListener("resize", handleResize);
    }
    deps.getReaderTextController().finishReaderPageLayout();
  };

  const initializePage = async (
    activeReaderViewStore: ReaderViewStore,
    initializeFormattingOptions?: () => Promise<void> | void,
    initializeSyncAudio?: (syncAudioReaderViewStore: ReaderViewStore) => void,
    initializeReadAloud?: (readAloudReaderViewStore: ReaderViewStore) => void
  ) => {
    const pageDataPatch: Record<string, any> = {
      selectedTabName: deps.getCurrentTabName() ?? sidebarSettings.getTab(),
    };
    deps.patchPageData(pageDataPatch);

    body?.attr("id", pageName);
    readerTextStore?.setClickMode("translate");

    await initializeFormattingOptions?.();

    initializeReadAloud?.(activeReaderViewStore);

    if (deps.getPageData().contextTranslationUsage) {
      setupContextTranslationStripSubscription();
    }

    if (deps.getPageData().showSignedOutTopBar) {
      if (!localStorage?.getItem("referrer")) {
        localStorage?.setItem("referrer", "readerPage");
      }
    }

    if (
      !["plus", "freeForever"].includes(
        stores.user.state.value.subscriptionPlan
      ) &&
      stores.user.featureFlags.value?.["betterAIModel"]
    ) {
      notificationBar.showMessage({
        htmlMessage:
          "Thanks for trying out the better AI model option! If you want " +
          "to continue using this, please <a href='/account'>upgrade to Premium Plus</a>. Otherwise, " +
          "you will now revert to using the standard AI model.",
        time: 9999999,
      });
      stores.user.setFeature("betterAIModel", false);
    }

    initializeSyncAudio?.(activeReaderViewStore);

    disposers.push(
      stores.user.onChange(
        (a) => a.currentLearningLanguage,
        () => {
          if (
            /^(on|readyToLearn)$/.test(
              stores.user.state.value.readerWordHighlights
            )
          ) {
            deps.requestReaderPageRerender();
          }
        }
      )
    );

    if (!(await localAPI.isLocalMode())) {
      disposers.push(
        stores.remoteUserWords.onTranslationChange((model) => {
          readerTextStore?.updateTranslations(
            model.state.value.word,
            model.state.value.translation
          );
        }),
        stores.remoteUserWords.onWordsDeleted((wordIDs) => {
          readerTextStore?.untranslateDeletedWords(wordIDs);
        })
      );
    }
  };

  const setupInitialReaderPage = async (
    initialized: boolean,
    currentReaderViewStore: ReaderViewStore,
    callbacks: CompletePreactRenderCallbacks
  ) => {
    if (!initialized) {
      await initializePage(
        currentReaderViewStore,
        callbacks.initializeFormattingOptions,
        callbacks.initializeSyncAudio,
        callbacks.initializeReadAloud
      );
      const fieldsTriggeringPageRerender = [
        "readerSentenceBreaks",
        "readerContextTranslations",
        "readerWordHighlights",
        "readerMergePhrases",
        "readerTranslations",
        "readerTransliterations",
      ];
      disposers.push(
        stores.user.onChange(
          (a) =>
            fieldsTriggeringPageRerender.map(
              (field) => a[field as keyof typeof a]
            ),
          () => {
            deps.requestReaderPageRerender();
          }
        )
      );
    }
  };

  const completePreactRender = async (
    preparedRender: PreparedReaderPageRender,
    callbacks: CompletePreactRenderCallbacks = {}
  ) => {
    const options = deps.getOptions();
    if (preparedRender.cancelled || preparedRender.errorMessageLines) {
      deps.getRenderedCallback()?.();
      return;
    }

    const initialized = preparedRender.initialized;
    const currentReaderViewStore = preparedRender.currentReaderViewStore;

    await setupInitialReaderPage(
      initialized,
      currentReaderViewStore,
      callbacks
    );

    callbacks.updateSyncAudioOptions?.(options);

    setupPageTextIsland(options);
    setupReaderMode(options);

    if (!options.endOfText) {
      setupTextInteractions(options);
    }

    finishReaderPageLayout(initialized);
  };

  const close = () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("resize", handleResize);

    if (body?.attr("id") === pageName) {
      body.removeAttr("id");
    }

    readerViewStore = null;
    readerTextStore = null;

    for (const className of [
      "colorDark",
      "colorSepia",
      "showContextTranslationStrip",
    ]) {
      body?.removeClass(className);
    }

    body?.removeAttr("data-clickMode");

    for (const dispose of disposers) dispose();
    disposers.length = 0;
  };

  return {
    close,
    completePreactRender,
    preparePreactRender,
  };
};
